import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import AsyncSelect from 'react-select/async';

import { LAG_DAYS_LIMIT, lagMessage, lagSchema, planLabel } from './linkedActivity';

import Button from 'CommonComponents/Button';

import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

export const DEFAULT_DEPENDENCY_TYPE = 'finish_to_start';

const selectStyles = {
    control: (baseStyles) => ({
        ...baseStyles,
        minHeight: 32,
        backgroundColor: 'rgb(228 228 228 / 0.5)',
    }),
};

const CandidateLabel = ({ candidate }) => (
    <div className="tw-flex tw-flex-col">
        <span className={candidate.creates_loop ? 'tw-text-charcoal-50' : 'tw-text-charcoal'}>{candidate.description}</span>
        <span className="tw-text-xs tw-text-charcoal-50">
            {planLabel(candidate)}
            {candidate.creates_loop && ` · ${$t('would create a loop')}`}
        </span>
    </div>
);

/**
 * Inline row for picking a new predecessor. In edit mode candidates come from the activity, which
 * also flags those that would close a loop; in add mode they come from the category.
 */
const AddPredecessorRow = ({ mode, activityId, categoryId, excludeIds, disabled = false, onAdd, onCancel }) => {
    const { dependencyTypes, searchProjectActivityDependencyCandidatesUrl, searchProjectCategoryDependencyCandidatesUrl } = useContext(ProjectPlanContext);

    const [candidate, setCandidate] = useState(null);
    const [type, setType] = useState(DEFAULT_DEPENDENCY_TYPE);
    const [lag, setLag] = useState('0');
    const [lagError, setLagError] = useState(null);
    const [defaultOptions, setDefaultOptions] = useState([]);
    const [loadingDefaults, setLoadingDefaults] = useState(true);

    const url =
        mode === 'edit'
            ? searchProjectActivityDependencyCandidatesUrl.replace('ACTIVITY_ID', activityId)
            : searchProjectCategoryDependencyCandidatesUrl.replace('CATEGORY_ID', categoryId);

    // Read through a ref so staging a row does not rebuild the debounced loader.
    const excludeIdsRef = useRef(excludeIds);
    excludeIdsRef.current = excludeIds;

    const withoutExcluded = (items) => items.filter((item) => !excludeIdsRef.current.includes(item.id));

    // The parent rebuilds excludeIds on every render. A new defaultOptions array makes react-select
    // reset its options, so this list only changes when the ids themselves do.
    const excludeKey = excludeIds.join(',');
    const visibleDefaultOptions = useMemo(() => withoutExcluded(defaultOptions), [defaultOptions, excludeKey]);

    // The unfiltered list loads straight away; only typed searches are debounced.
    useEffect(() => {
        let cancelled = false;

        setLoadingDefaults(true);
        axios
            .get(url, { params: { search: '' } })
            .then((response) => !cancelled && setDefaultOptions(response.data?.data || []))
            .catch(() => !cancelled && setDefaultOptions([]))
            .finally(() => !cancelled && setLoadingDefaults(false));

        return () => {
            cancelled = true;
        };
    }, [url]);

    const loadOptions = useMemo(
        () =>
            debounce((search, callback) => {
                axios
                    .get(url, { params: { search } })
                    .then((response) => callback(withoutExcluded(response.data?.data || [])))
                    .catch(() => callback([]));
            }, 300),
        [url],
    );

    useEffect(() => () => loadOptions.cancel(), [loadOptions]);

    const add = () => {
        if (!candidate) {
            return;
        }

        if (!lagSchema().isValidSync(lag)) {
            setLagError(lagMessage());

            return;
        }

        onAdd({ predecessor_id: candidate.id, type, lag_days: Number(lag), activity: candidate });
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-2 tw-rounded tw-border tw-border-grey tw-p-3">
            <div className="tw-flex tw-items-start tw-gap-x-3">
                <div className="tw-grow tw-min-w-0">
                    <AsyncSelect
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        aria-label={$t('Predecessor')}
                        placeholder={$t('Search activities')}
                        cacheOptions={false}
                        defaultOptions={visibleDefaultOptions}
                        isLoading={loadingDefaults || undefined}
                        isDisabled={disabled}
                        loadOptions={loadOptions}
                        filterOption={null}
                        value={candidate}
                        onChange={setCandidate}
                        getOptionValue={(option) => String(option.id)}
                        getOptionLabel={(option) => option.description}
                        isOptionDisabled={(option) => option.creates_loop}
                        formatOptionLabel={(option) => <CandidateLabel candidate={option} />}
                        noOptionsMessage={() => $t('No activities found')}
                    />
                </div>
                <div className="tw-w-40">
                    <select
                        className="select-input tw-border-0"
                        aria-label={$t('Type')}
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        disabled={disabled}
                    >
                        {Object.entries(dependencyTypes).map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="tw-w-24">
                    <input
                        type="number"
                        className={lagError ? 'text-input tw-border tw-border-red-500' : 'text-input tw-border-0'}
                        aria-label={$t('Lag (days)')}
                        min={-LAG_DAYS_LIMIT}
                        max={LAG_DAYS_LIMIT}
                        step={1}
                        value={lag}
                        onChange={(e) => {
                            setLag(e.target.value);
                            setLagError(null);
                        }}
                        disabled={disabled}
                    />
                </div>
                <Button variant="primary" onClick={add} disabled={disabled || !candidate}>
                    {$t('Add')}
                </Button>
                <Button variant="secondary" onClick={onCancel} disabled={disabled}>
                    {$t('Cancel')}
                </Button>
            </div>
            {!!lagError && <div className="tw-text-red-500 tw-text-sm">{lagError}</div>}
        </div>
    );
};

export default AddPredecessorRow;
